import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Alert,
    Box,
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Constants from '../../assests/Constants/constants.js';
import DocumentService from '../../Services/documents.service.js';
import {
    compareItemRows,
    formatLinkAmount,
    formatLinkDate,
    getStatusClassName,
    getStatusLabelKey,
    RELATION_TYPE,
} from './documentLifecycle.js';
import LinkDocumentItemsPanel from './SubComponents/LinkDocumentItemsPanel.jsx';
import LinkDocumentMatchChip from './SubComponents/LinkDocumentMatchChip.jsx';
import styles from './PendingLinksSummary.module.scss';

const LinkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M10.6 13.4a3.5 3.5 0 0 0 4.95 0l3.54-3.54a3.5 3.5 0 0 0-4.95-4.95l-1.06 1.06"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M13.4 10.6a3.5 3.5 0 0 0-4.95 0l-3.54 3.54a3.5 3.5 0 0 0 4.95 4.95l1.06-1.06"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const typeLabel = (documentType) => Constants.TransactionDocuments?.[documentType] ?? '';

const statusChip = (t, link) => {
    const row = { processingStatus: link?.processingStatus, transactionDocumentType: link?.documentType };
    const className = getStatusClassName(row);
    const color = className === 'processed' ? 'success' : className === 'partially-processed' ? 'warning' : 'error';
    return <Chip size="small" variant="outlined" color={color} label={t(getStatusLabelKey(row))} />;
};

const comparisonResultKey = (result) => ({
    match: 'LinkDocumentsItemRowMatch',
    qtyDiff: 'LinkDocumentsItemRowQtyDiff',
    notIncluded: 'LinkDocumentsItemRowNotIncluded',
    different: 'LinkDocumentsItemRowDifferent',
    mismatch: 'LinkDocumentsItemRowMismatch',
}[result] || 'LinkDocumentsItemRowMismatch');

/**
 * "שיוך מסמכים" button + summary of the pending links chosen for a document being created.
 * Match chips, expandable item snapshots and comparison against the draft lines use the same
 * logic as LinkDocumentsModal.
 */
const PendingLinksSummary = ({
    direction = 'rtl',
    pendingLinks = [],
    total = 0,
    validation = { overAllocated: false },
    canOpen = false,
    disabled = false,
    fullWidth = true,
    detailsInModal = true,
    caseId,
    token,
    draftItems = [],
    onOpen,
    onRemove,
    onLinkItemsLoaded,
}) => {
    const { t } = useTranslation();
    const count = pendingLinks.length;
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [itemCache, setItemCache] = useState({});
    const itemCacheRef = useRef(itemCache);
    itemCacheRef.current = itemCache;

    useEffect(() => {
        if (count === 0) setSummaryOpen(false);
    }, [count]);

    const handleButtonClick = () => {
        if (detailsInModal && count > 0) {
            setSummaryOpen(true);
            return;
        }
        onOpen?.();
    };

    const handleEdit = () => {
        setSummaryOpen(false);
        onOpen?.();
    };

    const ensureItems = async (id) => {
        if (!id || !caseId || !token) return;
        const key = String(id);
        const existing = pendingLinks.find((l) => String(l.documentId) === key)?.items;
        if (Array.isArray(existing) && existing.length > 0) {
            if (!itemCacheRef.current[key]?.loaded) {
                const next = { loading: false, loaded: true, items: existing, error: '' };
                itemCacheRef.current = { ...itemCacheRef.current, [key]: next };
                setItemCache(itemCacheRef.current);
            }
            return;
        }
        if (itemCacheRef.current[key]?.loading || itemCacheRef.current[key]?.loaded) return;
        itemCacheRef.current = { ...itemCacheRef.current, [key]: { loading: true, loaded: false, items: [], error: '' } };
        setItemCache(itemCacheRef.current);
        try {
            const items = await DocumentService.GetDocumentItems(caseId, id, token);
            const list = Array.isArray(items) ? items : [];
            itemCacheRef.current = { ...itemCacheRef.current, [key]: { loading: false, loaded: true, items: list, error: '' } };
            setItemCache(itemCacheRef.current);
            onLinkItemsLoaded?.(id, list);
        } catch (err) {
            itemCacheRef.current = {
                ...itemCacheRef.current,
                [key]: { loading: false, loaded: true, items: [], error: err?.message || t('LinkDocumentsItemsLoadFailed') },
            };
            setItemCache(itemCacheRef.current);
        }
    };

    const toggleExpand = (event, id) => {
        event.stopPropagation();
        const key = String(id);
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
        ensureItems(id);
    };

    const button = (
        <button
            type="button"
            className={`${styles.trigger}${validation.overAllocated ? ` ${styles.danger}` : ''}`}
            onClick={handleButtonClick}
            disabled={disabled || (!canOpen && count === 0)}
        >
            <span className={styles.icon} aria-hidden="true"><LinkIcon /></span>
            <span className={styles.label}>
                {count > 0 ? `${t('LinkDocuments')} (${count})` : t('LinkDocuments')}
            </span>
        </button>
    );

    const summaryTable = (
        <>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell align="center">{t('DocumentType')}</TableCell>
                            <TableCell align="center">{t('Reference')}</TableCell>
                            <TableCell align="center">{t('Date')}</TableCell>
                            <TableCell align="center">{t('Amount')}</TableCell>
                            <TableCell align="center">{t('LinkDocumentsAvailableAmount')}</TableCell>
                            <TableCell align="center">{t('LinkDocumentsAmountToLink')}</TableCell>
                            <TableCell align="center">{t('Status')}</TableCell>
                            <TableCell align="center" padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pendingLinks.map((link) => {
                            const id = String(link.documentId);
                            const isOpen = !!expanded[id];
                            const cache = itemCache[id] || {};
                            const sourceItems = cache.items?.length ? cache.items : (link.items || []);
                            const comparison = isOpen && (cache.loaded || (link.items || []).length > 0)
                                ? compareItemRows(sourceItems, draftItems || [])
                                : [];
                            return (
                                <React.Fragment key={id}>
                                    <TableRow selected>
                                        <TableCell padding="checkbox">
                                            <IconButton size="small" onClick={(e) => toggleExpand(e, link.documentId)} aria-label={t('LinkDocumentsShowItems')}>
                                                {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="center">{typeLabel(link.documentType)}</TableCell>
                                        <TableCell align="center">{link.reference || '-'}</TableCell>
                                        <TableCell align="center">{formatLinkDate(link.documentDate) || '-'}</TableCell>
                                        <TableCell align="center">
                                            <LinkDocumentMatchChip candidate={link} relationType={link.relationType} />
                                            {' '}
                                            {formatLinkAmount(link.amount)}
                                        </TableCell>
                                        <TableCell align="center">{formatLinkAmount(link.availableAmount)}</TableCell>
                                        <TableCell align="center">{formatLinkAmount(link.appliedAmount)}</TableCell>
                                        <TableCell align="center">{statusChip(t, link)}</TableCell>
                                        <TableCell align="center" padding="checkbox">
                                            <Tooltip title={t('Delete')} arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={disabled}
                                                        onClick={() => onRemove?.(link.documentId)}
                                                        aria-label={t('Delete')}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    {isOpen && (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ backgroundColor: '#fafbff', py: 1 }}>
                                                <LinkDocumentItemsPanel
                                                    items={sourceItems}
                                                    loading={cache.loading}
                                                    error={cache.error}
                                                />
                                                {comparison.length > 0 && Number(link.relationType) !== RELATION_TYPE.PAYMENT_FOR && (
                                                    <Box sx={{ mt: 1, overflowX: 'auto' }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#304FFF' }}>
                                                            {t('LinkDocumentsItemComparison')}
                                                        </Typography>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell align="center">{t('Makat')}</TableCell>
                                                                    <TableCell align="center">{t('Name')}</TableCell>
                                                                    <TableCell align="center">{t('LinkDocumentsSourceQty')}</TableCell>
                                                                    <TableCell align="center">{t('LinkDocumentsTargetQty')}</TableCell>
                                                                    <TableCell align="center">{t('LinkDocumentsItemRowResult')}</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {comparison.map((row) => (
                                                                    <TableRow key={row.key}>
                                                                        <TableCell align="center">{row.makat || '—'}</TableCell>
                                                                        <TableCell align="center">{row.name || row.label}</TableCell>
                                                                        <TableCell align="center">{row.sourceQty}</TableCell>
                                                                        <TableCell align="center">{row.targetQty}</TableCell>
                                                                        <TableCell align="center">{t(comparisonResultKey(row.result))}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={styles.totalRow}>
                <Typography variant="body2" className={styles.totalLabel}>
                    {`${t('LinkDocumentsPendingTotal')}: `}
                </Typography>
                <Chip
                    size="small"
                    color={validation.overAllocated ? 'error' : 'primary'}
                    label={formatLinkAmount(total)}
                />
            </div>
            {validation.overAllocated && (
                <Alert severity="error" className={styles.overAllocatedAlert}>{t('LinkDocumentsOverAllocated')}</Alert>
            )}
        </>
    );

    const summaryDialog = detailsInModal ? (
        <Dialog
            open={summaryOpen && count > 0}
            onClose={() => setSummaryOpen(false)}
            maxWidth="md"
            fullWidth
            dir={direction}
        >
            <DialogTitle className={styles.dialogTitle}>
                {t('LinkDocumentsPendingTitle')}
            </DialogTitle>
            <DialogContent dividers className={styles.dialogContent}>{summaryTable}</DialogContent>
            <DialogActions className={styles.dialogActions}>
                <Button onClick={() => setSummaryOpen(false)}>{t('Close')}</Button>
                <Button variant="contained" onClick={handleEdit} disabled={disabled || !canOpen}>
                    {t('LinkDocuments')}
                </Button>
            </DialogActions>
        </Dialog>
    ) : null;

    const overAllocatedHint = detailsInModal && validation.overAllocated && count > 0 ? (
        <Alert
            severity="error"
            onClick={() => setSummaryOpen(true)}
            className={`${styles.overAllocatedHint}${fullWidth ? ` ${styles.overAllocatedHintFull}` : ''}`}
        >
            {t('LinkDocumentsOverAllocated')}
        </Alert>
    ) : null;

    if (!fullWidth) {
        return (
            <div className={`${styles.root} ${styles.inline}`} dir={direction}>
                {button}
                {overAllocatedHint}
                {summaryDialog}
            </div>
        );
    }

    return (
        <div className={`${styles.root} ${styles.full}`} dir={direction}>
            {button}
            {!detailsInModal && count > 0 && (
                <div className={styles.details}>{summaryTable}</div>
            )}
            {overAllocatedHint}
            {summaryDialog}
        </div>
    );
};

export default PendingLinksSummary;
